import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { prefersReducedMotion } from '@/lib/dom';
import css from './Boot.module.css';

/** How long the boot screen holds before advancing on its own. */
const HOLD_MS = 3500;
/** Matches the opacity transition in Boot.module.css. */
const FADE_MS = 300;

interface BootScreenProps {
  onDone: () => void;
}

/**
 * The black XP boot screen: logo, marching marquee, and a very short fuse.
 * Nobody visiting a personal site wants to sit through a boot sequence twice,
 * so any input at all skips it.
 */
export function BootScreen({ onDone }: BootScreenProps) {
  // Sampled once: the screen lives for a few seconds, so re-reading the media
  // query on change would only ever cause a pointless mid-boot restyle.
  const [reduced] = useState(prefersReducedMotion);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (leaving) return;

    const skip = () => setLeaving(true);
    const timer = window.setTimeout(skip, HOLD_MS);

    // `pointerdown` covers mouse/touch/pen the instant contact is made;
    // `click` is the keyboard-activation and synthetic-event fallback.
    window.addEventListener('pointerdown', skip);
    window.addEventListener('click', skip);
    window.addEventListener('keydown', skip);

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('pointerdown', skip);
      window.removeEventListener('click', skip);
      window.removeEventListener('keydown', skip);
    };
  }, [leaving]);

  useEffect(() => {
    if (!leaving) return;
    // With reduced motion the fade is suppressed anyway, so don't sit on a
    // transition that will never be seen.
    const timer = window.setTimeout(onDone, reduced ? 0 : FADE_MS);
    return () => window.clearTimeout(timer);
  }, [leaving, reduced, onDone]);

  return (
    <div className={clsx(css.boot, leaving && css.bootLeaving)}>
      <div className={css.bootInner}>
        <img
          className={css.bootLogo}
          src="/assets/ui/logo_on_dark.png"
          width={220}
          height={161}
          alt=""
          draggable={false}
        />

        <div className={css.bootBar} role="progressbar" aria-label="Starting up">
          <div className={clsx(css.bootBlocks, reduced && css.bootBlocksStill)}>
            <span className={css.bootBlock} />
            <span className={css.bootBlock} />
            <span className={css.bootBlock} />
          </div>
        </div>

        <p className={css.bootHint}>Please wait&hellip;</p>
      </div>

      <div className={css.bootBrand}>teon.io</div>
    </div>
  );
}
